import type { ThemePreference } from '../types';

// The concrete theme actually applied to the DOM. 'system' resolves to one of
// these via the OS `prefers-color-scheme` media query.
export type ResolvedTheme = 'light' | 'dark';

// Mirrors the scheduler-view persistence pattern (`data/schedulerView.ts`): the
// user's choice is cached locally for instant, no-flash application on the next
// load (the inline script in index.html reads the same key before React mounts),
// and separately synced to their account by ThemeContext.
export const THEME_STORAGE_KEY = 'gooey.theme';

function isThemePreference(value: unknown): value is ThemePreference {
  return value === 'light' || value === 'dark' || value === 'system';
}

// Defaults to 'light' when nothing is saved — a fresh user with no preference
// gets light, not the OS scheme (matches the account-side default). 'system'
// only applies once the user explicitly chooses it.
export function readStoredTheme(): ThemePreference {
  if (typeof window === 'undefined') return 'light';
  const stored = window.localStorage.getItem(THEME_STORAGE_KEY);
  return isThemePreference(stored) ? stored : 'light';
}

export function writeStoredTheme(theme: ThemePreference): void {
  if (typeof window === 'undefined') return;
  window.localStorage.setItem(THEME_STORAGE_KEY, theme);
}

export function systemPrefersDark(): boolean {
  if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') {
    return false;
  }
  return window.matchMedia('(prefers-color-scheme: dark)').matches;
}

export function resolveTheme(pref: ThemePreference): ResolvedTheme {
  if (pref === 'system') return systemPrefersDark() ? 'dark' : 'light';
  return pref;
}
